import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { setPageTitle } from 'store/actions/header';
import { useBlogService } from 'hooks/api/agents/content/blog';
import { addBlogSearchResultsToStore, addManyBlogsToStore } from 'store/actions/blog';
import styles from './list.module.css';

const PAGE_SIZE = 15;
const STATUS_LABELS = { published: 'Published', draft: 'Draft', archived: 'Archived' };
const STATUS_CLASS = {
  published: 'statusPublished',
  draft: 'statusDraft',
  archived: 'statusArchived'
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

export const BlogListPage = ({
  selected_id,
  is_search = false,
  search_keyword,
  refresh_key = 0,
  onSelect = () => {},
  onRefreshComplete = () => {}
}) => {
  const dispatch = useDispatch();
  const { fetchBlogs, searchBlogs } = useBlogService();
  const blogs_in_store = useSelector((state) => state.blogs);

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'Content', path: '.' }]));
    handleDataRequest(0, 15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const parsed_blogs = Object.values(blogs_in_store).sort(
      (a, b) => new Date(b.time_stamp) - new Date(a.time_stamp)
    );
    setBlogs(() => parsed_blogs);
  }, [blogs_in_store]);

  useEffect(() => {
    handleDataRequest(page, 15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (!refresh_key) return;
    handleRefreshRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh_key]);

  useEffect(() => {
    if (!search_keyword) return;
    handleSearchRequest('title,description', search_keyword, page, 15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_search, search_keyword]);

  const handleRefreshRequest = async () => {
    if (search_keyword) {
      await handleSearchRequest('title,description', search_keyword, page, 15);
    } else {
      await handleDataRequest(page, 15);
    }
    onRefreshComplete();
  };

  const handleItemClick = (payload) => {
    onSelect(payload);
  };

  const handleDataRequest = async (
    page,
    population = 50,
    options = { filter_query: '&', sort_query: '' }
  ) => {
    try {
      setLoading(true);
      const query = '';
      const sort_query = options.sort_query || 'sort_by=-created_on';

      const { blogs } = await fetchBlogs({
        query_string: `page=${page}&population=${population}&${query}&${sort_query}`
      });

      if (((options.filter_query || options.sort_query) && page === 0) || page === 0) {
        return dispatch(addManyBlogsToStore(blogs));
      }
      dispatch(addManyBlogsToStore(blogs));
    } catch (e) {
      dispatch(addManyBlogsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchRequest = async (
    keys,
    keyword,
    page,
    population = 50,
    options = { filter_query: '&', sort_query: '' }
  ) => {
    if (!keys) return;
    try {
      setLoading(true);
      const query = '';
      const sort_query = options.sort_query || 'sort_by=-created_on';
      const { blogs } = await searchBlogs(keys, keyword, {
        query_string: `page=${page}&population=${population}&${query}&${sort_query}`
      });

      if (page === 0) return dispatch(addBlogSearchResultsToStore(blogs));
      dispatch(addManyBlogsToStore(blogs));
    } catch (e) {
      dispatch(addManyBlogsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const filtered = query.trim()
    ? blogs.filter(
        (b) =>
          (b.topic || '').toLowerCase().includes(query.toLowerCase()) ||
          (b.description || '').toLowerCase().includes(query.toLowerCase())
      )
    : blogs;

  const visible = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const can_prev = page > 0;
  const can_next = (page + 1) * PAGE_SIZE < filtered.length;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.searchBar}>
          <svg className={styles.searchIcon} viewBox="0 0 20 20" fill="none">
            <path
              d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
              fill="currentColor"
            />
          </svg>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search blogs..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(0);
            }}
          />
          {query && (
            <button className={styles.searchClear} onClick={() => setQuery('')}>
              &#x2715;
            </button>
          )}
        </div>
      </div>

      <div className={styles.list}>
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={styles.skeletonItem}>
              <Skeleton height={13} width="70%" style={{ marginBottom: 6 }} borderRadius={4} />
              <Skeleton height={11} width="90%" style={{ marginBottom: 4 }} borderRadius={4} />
              <Skeleton height={10} width="38%" borderRadius={4} />
            </div>
          ))
        ) : visible.length === 0 ? (
          <div className={styles.listEmpty}>
            {query ? `No results for "${query}"` : 'No blogs found'}
          </div>
        ) : (
          visible.map((blog) => (
            <div
              key={blog.id}
              className={`${styles.item} ${selected_id === blog.id ? styles.itemSelected : ''}`}
              onClick={() => handleItemClick(blog)}
            >
              <div className={styles.itemTitle}>{blog.topic || 'Untitled'}</div>
              {blog.description && <p className={styles.itemExcerpt}>{blog.description}</p>}
              <div className={styles.itemFooter}>
                {blog.status && (
                  <span
                    className={`${styles.statusBadge} ${
                      styles[STATUS_CLASS[blog.status] || 'statusDraft']
                    }`}
                  >
                    {STATUS_LABELS[blog.status] || blog.status}
                  </span>
                )}
                {blog.created_on && (
                  <span className={styles.itemDate}>{formatDate(blog.created_on)}</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className={styles.pagination}>
        <button
          className={styles.pageBtn}
          disabled={!can_prev}
          onClick={() => can_prev && setPage((p) => p - 1)}
        >
          &#8249;
        </button>
        <span className={styles.pageLabel}>Page {page + 1}</span>
        <button
          className={styles.pageBtn}
          disabled={!can_next}
          onClick={() => can_next && setPage((p) => p + 1)}
        >
          &#8250;
        </button>
      </div>
    </div>
  );
};
